import React, { useState, useEffect, useRef } from 'react';
import { backdropProps } from '../utils/backdropClose';
import { supabase } from '../supabase';
import { US_STATES } from '../constants/usStates';
import { MONTHS } from '../constants/months';
import { PICK_YEARS, FISCAL_YEARS, YESNO, BILL_TYPES, TAX_TYPES,
  pctToInput, pctParse, composeSeasonMY, parseSeasonMY,
  embedsCalendarYear, shiftYears, SEEDABLE_COLLECTOR_TEMPORAL } from '../constants/refFields';
import { entityKey } from '../utils/entityKey';
import { pageAll } from '../utils/pageAll';

// Add/Edit Collector — TWO-TRACK (RE / BPP) + VERSION-AWARE + a per-track installments editor.
//
// Shape mirrors the two-track importer exactly (ImportTemplateModalTwoTrack) so hand-created collectors
// are structurally identical to imported ones.
//
// Two-track: collectors carries re_/bpp_ pairs for tax_bill_season, fiscal_year and ratio (added by
// 2026-07-19_importer_two_track_columns) because the RE and BPP seasons genuinely differ (CA RE
// "October 2026" vs BPP "July 2026"). The pre-rebuild single-track tax_bill_season / assessment_ratio
// columns are INERT by design -- that migration leaves them unpopulated, so this modal never writes them.
//
// Versioning: collectors are grained (entity_id, value_year); collector_installments.collector_id was
// re-pointed to the ENTITY anchor and carries a NOT NULL tax_year. So installments are keyed on
// (entity_id, tax_year) -- never on the year-scoped collectors row id.
//
// BACKFILL SEEDING: `seed` is the nearest existing version of this same entity, used to create an empty
// prior year. Everything carries EXCEPT the fields whose stored value has a year baked in --
// re_/bpp_tax_bill_season ("October 1, 2026") and each installment's due_date (a calendar date). Those
// start blank; the record is marked seeded_from_year until they are entered, which is what the list's
// warning indicator keys on. Saving with blank due dates WARNS but is allowed, so Steph can carry
// everything in one pass and fill due dates from the actual bills in a second.
//
// Props: isDark; collector (null=add / row=edit); seed (nearest-year row to pre-fill from, add only);
//        assessor (drilled-in context -- read-only autofills);
//        defaultState; valueYear (screen-selected year, locked); onClose; onSuccess(row).
// NOTE the naming asymmetry: assessors call the second track pp_*, collectors call it bpp_*.
const TRACKS = [
  { key: 're',  label: 'Real Estate',                exemptField: 're_exempt' },
  { key: 'bpp', label: 'Business Personal Property', exemptField: 'pp_exempt' },
];
// Human labels for the blanked-field list in the seed banner.
const LABELS = Object.fromEntries(TRACKS.flatMap(({ key: t }) =>
  [['tax_bill_season', 'Tax Bill Season'], ['fiscal_year', 'Fiscal Year']]
    .map(([f, l]) => [`${t}_${f}`, `${t === 're' ? 'RE' : 'BPP'} ${l}`])));

export default function AddCollectorModal({ isDark, collector, seed, assessor, defaultState, valueYear, onClose, onSuccess }) {
  const isEdit = !!collector;
  const isSeeded = !collector && !!seed;   // creating a prior year pre-filled from another version
  const src = collector || seed || null;   // where the initial field values come from
  const lockedYear = valueYear ?? collector?.value_year ?? '';

  // Temporal fields whose calendar year this seed SHIFTS by the gap between the records ("October 2026" on 2026 →
  // "October 2025" on 2025 — Philip, 2026-10-09; they used to be blanked). Computed from the DATA, not a fixed list.
  // Installment due_dates are still always blanked (tracked via missingDueDates()).
  const yearShift = isSeeded ? (Number(lockedYear) - Number(seed.value_year)) || 0 : 0;
  const shiftedKeys = React.useMemo(() => {
    if (!isSeeded) return [];
    const out = [];
    TRACKS.forEach(({ key: t }) => SEEDABLE_COLLECTOR_TEMPORAL.forEach(f => {
      if (embedsCalendarYear(seed[`${t}_${f}`])) out.push(`${t}_${f}`);
    }));
    return out;
  }, [isSeeded, seed]);
  const [markVerified, setMarkVerified] = useState(false); // reopened flagged record — cleared explicitly
  // Context autofill (read-only, NOT collector-owned): the assessor whose view this was opened under.
  const ctxAssessorName = assessor?.name || '—';

  const [form, setForm] = useState(() => {
    const base = {
      name:              src?.name || '',
      state:             src?.state || defaultState || 'AL',
      discount_offered:  src?.discount_offered || '',
      nav_billed_on:     src?.nav_billed_on || 'split',
      url:               src?.url || '',
      mailing_address_1: src?.mailing_address_1 || '',
      mailing_address_2: src?.mailing_address_2 || '',
      mailing_city:      src?.mailing_city || '',
      mailing_state:     src?.mailing_state || '',
      mailing_zip:       src?.mailing_zip || '',
      is_cook:           !!src?.is_cook,
      re_exempt:         !!src?.re_exempt,
      pp_exempt:         !!src?.pp_exempt,
    };
    // On a seed, a calendar year inside a temporal value moves by the gap between the records; the rest carries.
    const carry = (t, f) => shiftYears(src?.[`${t}_${f}`] || '', yearShift);
    TRACKS.forEach(({ key: t }) => {
      const sea = parseSeasonMY(carry(t, 'tax_bill_season') || null);
      Object.assign(base, {
        [`${t}_tbs_month`]: sea.month, [`${t}_tbs_year`]: sea.year,
        [`${t}_fiscal_year`]: carry(t, 'fiscal_year'),
        [`${t}_ratio`]: pctToInput(src?.[`${t}_ratio`]),   // numeric — no year
      });
    });
    return base;
  });
  const [instRows, setInstRows] = useState([]);     // {rowKey, kind, installment_number, bill_type, tax_type, ratio, split_pct, due_date, adjustment_type, adjustment_pct, _adjOpen}
  const [adjOptions, setAdjOptions] = useState([]); // Adjustment Type labels from the lookup
  const [savedId, setSavedId] = useState(collector?.id || null);              // collectors ROW id (year-scoped)
  const [savedEntityId, setSavedEntityId] = useState(collector?.entity_id || null); // ENTITY anchor — installments key on this
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const keyc = useRef(0);
  const mkRow = (kind, seed = {}) => ({
    rowKey: ++keyc.current, kind,
    installment_number: seed.installment_number ?? '',
    bill_type: seed.bill_type ?? '',
    tax_type: seed.tax_type ?? '',
    ratio: seed.ratio != null ? pctToInput(seed.ratio) : '',
    split_pct: seed.split_pct != null ? String(seed.split_pct) : '100', // payment split — always present, default 100
    adjustment_type: seed.adjustment_type ?? '',                        // '' = Standard Payment (no adjustment)
    adjustment_pct: seed.adjustment_pct != null ? String(seed.adjustment_pct) : '',
    _adjOpen: !!seed.adjustment_type,                                   // adjustment sub-row visible?
    due_date: seed.due_date ?? '',
  });

  useEffect(() => {
    supabase.from('lookup_options').select('label, lookup_lists!inner(name)')
      .eq('lookup_lists.name', 'Adjustment Type').eq('active', true).order('sort_order')
      .then(({ data }) => setAdjOptions((data || []).map(o => o.label)));
  }, []);

  // Installments hang off the ENTITY + the selected year, not the year-scoped collectors row.
  // When seeding, read the SEED year's rows instead and blank each due_date -- a due date is an actual
  // calendar date, so carrying "2026-11-01" into a 2025 record would bake in the wrong year.
  useEffect(() => {
    const eid = src?.entity_id;
    const readYear = isSeeded ? Number(seed.value_year) : Number(lockedYear);
    if (!eid || !lockedYear) { setInstRows([]); return; }
    supabase.from('collector_installments').select('*')
      .eq('collector_id', eid).eq('tax_year', readYear)
      .order('kind', { ascending: true }).order('installment_number', { ascending: true })
      .then(({ data }) => setInstRows((data || []).map(r => mkRow(r.kind, isSeeded ? { ...r, due_date: '' } : r))));
  }, [collector, seed, lockedYear]);   // eslint-disable-line react-hooks/exhaustive-deps

  const c = isDark
    ? { card: '#2F3854', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', input: 'var(--ink)', inputBorder: '#434C68' }
    : { card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', input: '#ffffff', inputBorder: '#c8ccd8' };
  const s = {
    label:    { display: 'block', fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' },
    input:    { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btn:      { padding: '9px 18px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding: '7px 12px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '12px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    sectionH: { fontSize: '12px', fontWeight: '700', color: c.text, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '20px 0 12px', paddingBottom: '6px', borderBottom: `1px solid ${c.border}` },
    colH:     { fontSize: '10px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.05em' },
    chk:      { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: c.text, cursor: 'pointer' },
  };

  const set = (f, v) => setForm(prev => ({ ...prev, [f]: v }));
  const num = (v) => (v === '' || v == null ? null : Number(v));
  const txt = (v) => (v && String(v).trim() ? String(v).trim() : null);
  // Ratio columns here are NUMERIC (unlike assessors', which are text), so a non-numeric passthrough
  // from pctParse ("Class 1: 30%…") must land as null rather than fail the insert.
  const ratioNum = (v) => { const p = pctParse(v); return p != null && /^\d*\.?\d+$/.test(p) ? Number(p) : null; };

  // county is NOT written — importer-only (dedup key); preserved on update by omission. active is omitted
  // so new rows take the DB default and edits keep any retire state. The inert single-track
  // tax_bill_season / assessment_ratio columns are deliberately never written.
  const collectorFields = () => {
    const out = {
      name: txt(form.name), state: form.state || null,
      value_year: num(lockedYear),
      discount_offered: txt(form.discount_offered),
      url: txt(form.url),
      mailing_address_1: txt(form.mailing_address_1), mailing_address_2: txt(form.mailing_address_2),
      mailing_city: txt(form.mailing_city), mailing_state: txt(form.mailing_state), mailing_zip: txt(form.mailing_zip),
      is_cook: !!form.is_cook, re_exempt: !!form.re_exempt, pp_exempt: !!form.pp_exempt,
    };
    // nav_billed_on (2026-10-16_collectors_nav_billed_on.sql). Written when the loaded record has the column, or when it
    // is set off the default — so a save before that migration has run still works, and a new collector left at
    // 'split' takes the column's default.
    if ((src && 'nav_billed_on' in src) || form.nav_billed_on !== 'split') out.nav_billed_on = form.nav_billed_on;
    // An exempt track stores NULLs across its columns (matches the importer's `exempt ? null : …`).
    TRACKS.forEach(({ key: t, exemptField }) => {
      const ex = !!form[exemptField];
      out[`${t}_tax_bill_season`] = ex ? null : composeSeasonMY(form[`${t}_tbs_month`], form[`${t}_tbs_year`]);
      out[`${t}_fiscal_year`]     = ex ? null : txt(form[`${t}_fiscal_year`]);
      out[`${t}_ratio`]           = ex ? null : ratioNum(form[`${t}_ratio`]);
    });
    // Seeded provenance. On a seed: flagged while any year was shifted (a shifted year is a guess until someone
    // checks it) or any due date is missing. On a reopened flagged record: cleared only when Steph ticks
    // "verified" — a blank may be legitimately empty rather than pending, so it cannot be inferred.
    out.seeded_from_year = isSeeded
      ? ((shiftedKeys.length === 0 && missingDueDates() === 0) ? null : Number(seed.value_year))
      : (markVerified ? null : (collector?.seeded_from_year ?? null));
    return out;
  };

  const missingDueDates = () => instRows.filter(r => !String(r.due_date || '').trim()).length;

  // ── installment box helpers ──
  const boxRows = (kind) => instRows.filter(r => r.kind === kind);
  const addRow = (kind) => {
    if (boxRows(kind).length >= 4) return;
    setInstRows(prev => [...prev, mkRow(kind, { installment_number: boxRows(kind).length + 1 })]);
  };
  const removeRow = (rowKey) => setInstRows(prev => prev.filter(r => r.rowKey !== rowKey));
  const setRow = (rowKey, field, val) => setInstRows(prev => prev.map(r => r.rowKey === rowKey ? { ...r, [field]: val } : r));
  const patchRow = (rowKey, patch) => setInstRows(prev => prev.map(r => r.rowKey === rowKey ? { ...r, ...patch } : r));

  // Resolve this office's entity_id the way the importer would: state-scoped, on the office-suffix-stripped
  // CORE name. Returns an existing entity_id, or mints one + inserts its anchor row.
  async function resolveEntityId(name, state) {
    const key = entityKey(name);
    // pageAll — page past the 1000-row cap so a big state can't truncate the match and mint a duplicate collector.
    const data = await pageAll(() => supabase.from('collectors').select('entity_id,name').eq('state', state));
    const hit = (data || []).find(x => entityKey(x.name) === key);
    if (hit) return hit.entity_id;
    const eid = crypto.randomUUID();
    const { error: ae } = await supabase.from('collector_entities').insert({ entity_id: eid });
    if (ae) throw ae;
    return eid;
  }

  async function handleSave() {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.state) { setError('State is required.'); return; }
    if (!lockedYear) { setError('No value year selected.'); return; }
    // When an adjustment is set on an installment, its % is required. Block the save and point at
    // the exact box + row (per-kind 1-based position, or its installment number when set).
    const KIND_LABEL = { RE: 'Real Estate', PP: 'Personal Property' };
    for (const kind of ['RE', 'PP']) {
      const rows = instRows.filter(r => r.kind === kind);
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        if (r._adjOpen && String(r.adjustment_type).trim() && !String(r.adjustment_pct).trim()) {
          setError(`${KIND_LABEL[kind]} installment ${r.installment_number || i + 1}: adjustment % required.`);
          return;
        }
      }
    }
    // Blank due dates WARN but do not block — Steph carries everything in one pass and fills due dates
    // from the actual bills in a second. Confirming leaves the record flagged so the list still shows it.
    const nMissing = missingDueDates();
    if (nMissing > 0 && !window.confirm(
      `${nMissing} installment${nMissing === 1 ? ' has' : 's have'} no due date for ${lockedYear}.\n\n` +
      'Save anyway? The record stays flagged as incomplete so you can find it again from the list.')) return;
    setSaving(true); setError('');
    try {
      let id = savedId, eid = savedEntityId;
      if (id) {
        const { error: e } = await supabase.from('collectors').update(collectorFields()).eq('id', id);
        if (e) throw e;
      } else {
        eid = await resolveEntityId(form.name, form.state);
        const { data, error: e } = await supabase.from('collectors').insert({ ...collectorFields(), entity_id: eid }).select().single();
        if (e) throw e;
        id = data.id; setSavedId(id); setSavedEntityId(eid); // a retry now updates instead of inserting a duplicate
      }
      // Clean-replace this ENTITY's installments FOR THIS YEAR ONLY — other years are untouched.
      const rows = instRows.map(r => {
        const hasAdj = r._adjOpen && !!txt(r.adjustment_type);
        return {
          collector_id: eid, tax_year: Number(lockedYear), kind: r.kind,
          installment_number: num(r.installment_number),
          bill_type: txt(r.bill_type), tax_type: txt(r.tax_type),
          ratio: ratioNum(r.ratio),
          split_pct: String(r.split_pct).trim() === '' ? 100 : Number(r.split_pct),
          adjustment_type: hasAdj ? txt(r.adjustment_type) : null,
          adjustment_pct: hasAdj && String(r.adjustment_pct).trim() !== '' ? Number(r.adjustment_pct) : null,
          due_date: r.due_date || null,
        };
      });
      const { error: delErr } = await supabase.from('collector_installments').delete()
        .eq('collector_id', eid).eq('tax_year', Number(lockedYear));
      if (delErr) throw new Error('Collector saved, but updating installments failed (' + delErr.message + '). Your rows are still here — Save again to retry.');
      if (rows.length) {
        const { error: insErr } = await supabase.from('collector_installments').insert(rows);
        if (insErr) throw new Error('Collector saved, but installments failed to write (' + insErr.message + '). Your rows are still here — Save again to retry.');
      }
      const { data: fresh } = await supabase.from('collectors').select('*').eq('id', id).single();
      onSuccess(fresh || { id, entity_id: eid, ...collectorFields() });
    } catch (e) {
      // 23505 on (entity_id, value_year) = this office already has a row for this year.
      setError(e?.code === '23505'
        ? `${form.name.trim()} already has a ${lockedYear} record. Edit that one instead of adding a second.`
        : (e?.message || 'Save failed.'));
    } finally {
      setSaving(false);
    }
  }

  const pick = (label, field, opts, extra = {}) => (
    <div><label style={s.label}>{label}</label>
      <select style={{ ...s.input, ...extra }} value={form[field]} onChange={e => set(field, e.target.value)}>
        <option value="">—</option>{opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  // Per-track header block. When the track is exempt the inputs collapse to a single note — its columns
  // are written as NULL, so showing editable fields would imply data that will not be saved.
  const trackSection = (t, exemptField) => {
    if (form[exemptField]) {
      return <div style={{ fontSize: '13px', color: c.muted, fontStyle: 'italic', marginBottom: '14px' }}>Exempt — this track's season, fiscal year and ratio are stored as empty.</div>;
    }
    return (
      <>
        {/* Tax Bill Season is month + year only — the stored value carries no day (matches the detail). */}
        <div><label style={s.label}>Tax Bill Season</label>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
            <select style={s.input} value={form[`${t}_tbs_month`]} onChange={e => set(`${t}_tbs_month`, e.target.value)}>
              <option value="">Month</option>{MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <select style={{ ...s.input, maxWidth: '96px' }} value={form[`${t}_tbs_year`]} onChange={e => set(`${t}_tbs_year`, e.target.value)}>
              <option value="">Year</option>{PICK_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', marginBottom: '14px' }}>
          {pick('Fiscal Year', `${t}_fiscal_year`, FISCAL_YEARS)}
          <div><label style={s.label}>Assessment Ratio</label>
            <input style={s.input} value={form[`${t}_ratio`]} onChange={e => set(`${t}_ratio`, e.target.value)} placeholder="e.g. 25%" />
          </div>
        </div>
      </>
    );
  };

  const renderBox = (title, kind) => {
    const rows = boxRows(kind);
    return (
      <div style={{ border: `1px solid ${c.border}`, borderRadius: '8px', padding: '12px 14px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: rows.length ? '8px' : '0' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: c.text }}>{title}</div>
          <button type="button" onClick={() => addRow(kind)} disabled={rows.length >= 4}
            style={{ ...s.btnGhost, opacity: rows.length >= 4 ? 0.5 : 1, cursor: rows.length >= 4 ? 'not-allowed' : 'pointer' }}>+ Add</button>
        </div>
        {rows.map(r => (
          <div key={r.rowKey} style={{ border: `1px solid ${c.border}`, borderRadius: '7px', padding: '8px 10px', marginBottom: '8px' }}>
            {/* Main line: Installment # | Split % | Due Date */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ width: '54px' }}><div style={s.colH}>#</div><input type="number" placeholder="#" style={s.input} value={r.installment_number} onChange={e => setRow(r.rowKey, 'installment_number', e.target.value)} /></div>
              <div style={{ width: '84px' }}><div style={s.colH}>Split %</div><input placeholder="100" inputMode="decimal" style={s.input} value={r.split_pct} onChange={e => setRow(r.rowKey, 'split_pct', e.target.value.replace(/[^0-9.]/g, ''))} /></div>
              <div style={{ width: '150px' }}><div style={s.colH}>Due Date</div><input type="date" style={s.input} value={r.due_date} onChange={e => setRow(r.rowKey, 'due_date', e.target.value)} /></div>
              <button type="button" onClick={() => removeRow(r.rowKey)} title="Remove installment" style={{ ...s.btnGhost, padding: '6px 8px', textAlign: 'center', marginLeft: 'auto' }}>✕</button>
            </div>
            {/* Classification line: Bill Type | Tax Type | Ratio. Non-Value-Based is the Cook inst-1 case. */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
              <div style={{ flex: '1 1 130px', minWidth: '120px' }}><div style={s.colH}>Bill Type</div>
                <select style={s.input} value={r.bill_type} onChange={e => setRow(r.rowKey, 'bill_type', e.target.value)}>
                  <option value="">—</option>{BILL_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 130px', minWidth: '120px' }}><div style={s.colH}>Tax Type</div>
                <select style={s.input} value={r.tax_type} onChange={e => setRow(r.rowKey, 'tax_type', e.target.value)}>
                  <option value="">—</option>{TAX_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div style={{ width: '84px' }}><div style={s.colH}>Ratio</div><input placeholder="e.g. 25%" style={s.input} value={r.ratio} onChange={e => setRow(r.rowKey, 'ratio', e.target.value)} /></div>
            </div>
            {/* Adjustment sub-line — only when added (absence = standard payment) */}
            {!r._adjOpen ? (
              <button type="button" onClick={() => setRow(r.rowKey, '_adjOpen', true)} style={{ ...s.btnGhost, marginTop: '8px', fontSize: '11px', padding: '4px 10px' }}>+ Add adjustment</button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexWrap: 'wrap', marginTop: '8px', marginLeft: '14px' }}>
                <div style={{ fontSize: '14px', color: c.muted, alignSelf: 'center' }}>↳</div>
                <div style={{ flex: '1 1 160px', minWidth: '140px' }}><div style={s.colH}>Adjustment Type</div>
                  {/* Keep the saved value selectable even if it isn't in the curated lookup list — the
                      importer writes free-text types (e.g. "Equalization Factor"). Without this the
                      controlled select falls back to the first option and shows blank, so a save could
                      silently drop a real adjustment_type. */}
                  <select style={s.input} value={r.adjustment_type} onChange={e => setRow(r.rowKey, 'adjustment_type', e.target.value)}>
                    <option value="">— type —</option>
                    {(r.adjustment_type && !adjOptions.includes(r.adjustment_type) ? [r.adjustment_type, ...adjOptions] : adjOptions)
                      .map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div style={{ width: '84px' }}><div style={s.colH}>%</div><input placeholder="%" inputMode="decimal" style={s.input} value={r.adjustment_pct} onChange={e => setRow(r.rowKey, 'adjustment_pct', e.target.value.replace(/[^0-9.]/g, ''))} /></div>
                <button type="button" onClick={() => patchRow(r.rowKey, { _adjOpen: false, adjustment_type: '', adjustment_pct: '' })} title="Remove adjustment (back to standard)" style={{ ...s.btnGhost, padding: '6px 8px', textAlign: 'center' }}>✕</button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div {...backdropProps(onClose)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: c.card, borderRadius: '14px', padding: '28px', maxWidth: '680px', width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.3)', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', fontFamily: "'Public Sans',system-ui,sans-serif" }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '20px', fontWeight: '700', color: c.text }}>{isEdit ? 'Edit Collector' : 'Add Collector'}</div>
          <button onClick={onClose} type="button" style={{ background: 'none', border: 'none', color: c.muted, fontSize: '18px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
        </div>

        {isSeeded && (
          <div style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.45)', borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '12px', color: c.text, lineHeight: 1.5 }}>
            <strong>⚠ Seeded from {seed.value_year}.</strong> Every field and installment was copied from that year — review
            each one.{shiftedKeys.length > 0 && <> These named a year, which was <strong>moved from {seed.value_year} to
            {' '}{lockedYear}</strong> — check them: <strong>{shiftedKeys.map(k => LABELS[k] || k).join(', ')}</strong>.</>}
            {' '}Left <strong>blank</strong> and required for {lockedYear}: <strong>all installment due dates</strong>.
            This record stays flagged until the due dates are filled and it is opened and ticked “Verified”.
          </div>
        )}
        {isEdit && collector?.seeded_from_year != null && (
          <div style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.45)', borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '12px', color: c.text, lineHeight: 1.5 }}>
            <strong>⚠ Seeded from {collector.seeded_from_year} and not yet verified.</strong> Check the year-specific fields
            are right for {lockedYear}, then tick this to clear the flag.
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', cursor: 'pointer', fontWeight: '700' }}>
              <input type="checkbox" checked={markVerified} onChange={e => setMarkVerified(e.target.checked)} />
              Verified for {lockedYear}
            </label>
          </div>
        )}

        <div style={s.sectionH}>General</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 0.7fr 0.7fr', gap: '10px', marginBottom: '14px' }}>
          <div><label style={s.label}>Name *</label><input style={s.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Baldwin County" /></div>
          <div><label style={s.label}>Assessor Name</label><input style={{ ...s.input, opacity: 0.6, cursor: 'not-allowed' }} value={ctxAssessorName} readOnly title="From the assessor you opened this collector under" /></div>
          <div><label style={s.label}>State *</label>
            <select style={s.input} value={form.state} onChange={e => set('state', e.target.value)}>
              {Object.keys(US_STATES).map(code => <option key={code} value={code}>{code}</option>)}
            </select>
          </div>
          <div><label style={s.label}>Value Year</label><input style={{ ...s.input, opacity: 0.6, cursor: 'not-allowed' }} value={lockedYear} readOnly title="Set by the year selected on the main screen" /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
          {pick('Discount Offered', 'discount_offered', YESNO)}
          {/* Where the bill charges its non-ad valorem (flat) charges — the Taxes tab shows them there. Display only. */}
          <div><label style={s.label}>Non-ad valorem billed on</label>
            <select style={s.input} value={form.nav_billed_on} onChange={e => set('nav_billed_on', e.target.value)}>
              <option value="split">Split evenly across installments</option>
              <option value="installment_1">Installment 1</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '22px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <label style={s.chk}><input type="checkbox" checked={form.is_cook} onChange={e => set('is_cook', e.target.checked)} /> Cook County formula</label>
          <label style={s.chk}><input type="checkbox" checked={form.re_exempt} onChange={e => set('re_exempt', e.target.checked)} /> RE exempt</label>
          <label style={s.chk}><input type="checkbox" checked={form.pp_exempt} onChange={e => set('pp_exempt', e.target.checked)} /> BPP exempt</label>
        </div>
        <div style={{ marginBottom: '14px' }}><label style={s.label}>URL</label><input style={s.input} value={form.url} onChange={e => set('url', e.target.value)} placeholder="https://…" /></div>
        <div style={{ marginBottom: '14px' }}><label style={s.label}>Mailing Address 1</label><input style={s.input} value={form.mailing_address_1} onChange={e => set('mailing_address_1', e.target.value)} /></div>
        <div style={{ marginBottom: '14px' }}><label style={s.label}>Mailing Address 2</label><input style={s.input} value={form.mailing_address_2} onChange={e => set('mailing_address_2', e.target.value)} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', marginBottom: '14px' }}>
          <div><label style={s.label}>Mailing City</label><input style={s.input} value={form.mailing_city} onChange={e => set('mailing_city', e.target.value)} /></div>
          <div><label style={s.label}>Mailing State</label><input style={s.input} value={form.mailing_state} onChange={e => set('mailing_state', e.target.value)} /></div>
          <div><label style={s.label}>Mailing Zip</label><input style={s.input} value={form.mailing_zip} onChange={e => set('mailing_zip', e.target.value)} /></div>
        </div>

        {TRACKS.map(({ key, label, exemptField }) => (
          <React.Fragment key={key}>
            <div style={s.sectionH}>{label}</div>
            {trackSection(key, exemptField)}
          </React.Fragment>
        ))}

        <div style={s.sectionH}>Installments — {lockedYear}</div>
        {renderBox('Real Estate', 'RE')}
        {renderBox('Personal Property', 'PP')}

        {error && <div style={{ color: '#f87171', fontSize: '12px', margin: '10px 0' }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
          <button type="button" onClick={onClose} disabled={saving} style={s.btnGhost}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} style={s.btn}>{saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Collector')}</button>
        </div>
      </div>
    </div>
  );
}
